'use client';

import { Breadcrumb } from '@/components/common/breadcrumb';
import { menuBreadcrumbItems } from '@/config/menu-catalog';
import { PageHeader } from '@/components/common/page-header';
import { Button } from '@/components/ui/button';
import { ExpandableCard } from '@/components/ui/expandable-card';
import { useOrganization } from '@/contexts/organization-context';
import { useToast } from '@/hooks/use-toast';
import { apiClient } from '@/lib/api-client';
import {
  InOutPurchaseDto,
  PetrohqApiResponseListInOutPurchaseDto,
} from '@/lib/api/data-contracts';
import { useFilterStore } from '@/stores/filter-store';
import { Download } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import * as RealGrid from 'realgrid';
import { RealGridReact, RGDataColumn, RGDataField } from 'realgrid-react';

export function Purchase() {
  const { toast } = useToast();
  const { getAllStores } = useOrganization();
  // 온디맨드 방식: API 호출 시점에만 필터 상태 읽기

  // 상태 관리
  const [purchaseData, setPurchaseData] = useState<InOutPurchaseDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // 매입자료 데이터 가져오기 함수
  const fetchPurchaseData = useCallback(async () => {
    setIsLoading(true);
    try {
      // API 호출 시점에 현재 필터 상태 읽기
      const filters = useFilterStore.getState();

      const storeCodeList =
        filters.store.length > 0 ? filters.store : getAllStores();

      // 중앙집중식 파라미터 구축 사용
      const params = filters.buildApiParams('purchase', storeCodeList);

      const response = await apiClient.get('inout/purchase', {
        searchParams: params,
      });
      const data =
        (await response.json()) as PetrohqApiResponseListInOutPurchaseDto;

      if (data.result) {
        setPurchaseData(data.result);
      }
    } catch (error) {
      console.error('Purchase data fetch error:', error);
      toast({
        title: '조회 실패',
        description: '매입자료 데이터를 불러오는 중 오류가 발생했습니다',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [getAllStores, toast]);

  // 조회 버튼 클릭 핸들러
  const handleSearch = () => {
    fetchPurchaseData();
  };

  // 초기 데이터 로드
  useEffect(() => {
    fetchPurchaseData();
  }, []);

  // 엑셀 내보내기
  const handleExcelExport = () => {
    toast({
      title: '준비 중',
      description: '엑셀 내보내기 기능을 준비하고 있습니다',
    });
  };


  const onInitialized = (grid: RealGrid.GridView) => {
    grid.groupBy(['oilName']);
  };

  return (
    <div className='h-full'>
      <ExpandableCard
        title='매입자료'
        breadcrumb={
          <Breadcrumb items={menuBreadcrumbItems('purchase')} />
        }
        pageHeader={
          <PageHeader
            onSearch={handleSearch}
            isLoading={isLoading}
            actions={
              <Button variant='outline' size='sm' onClick={handleExcelExport}>
                <Download className='h-4 w-4 mr-2' />
                엑셀
              </Button>
            }
          />
        }
        fill
      >
        <div
          className='border rounded-lg flex-1 min-h-0'
        >
          <div className='w-full h-full'>
            <RealGridReact
              gridProps={{
                display: { fitStyle: RealGrid.GridFitStyle.FILL },
                checkBar: { visible: false },
                stateBar: { visible: false },
                edit: { readOnly: true, editable: false },
                footer: { visible: true },
                grouping: { enabled: true },
              }}
              rows={purchaseData}
              onInitialized={onInitialized}
            >
              <RGDataField
                fieldName={'compCd'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'compName'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'storeCd'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'storeName'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'closedt'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'closecnt'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'saleNo'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'dealName'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'cardNo2'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'qty'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'cost'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'amount'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'oilType'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'oilName'}
                dataType={RealGrid.ValueType.TEXT}
              />

              <RGDataColumn
                fieldName={'storeName'}
                header={{ text: '매장' }}
                width={120}
              />
              <RGDataColumn
                fieldName={'closedt'}
                header={{ text: '영업일' }}
                width={80}
              />
              <RGDataColumn
                fieldName={'closecnt'}
                header={{ text: '차수' }}
                width={50}
              />
              <RGDataColumn
                fieldName={'saleNo'}
                header={{ text: '순번' }}
                width={60}
                numberFormat='#,##0'
              />
              <RGDataColumn
                fieldName={'dealName'}
                header={{ text: '매입처' }}
                width={100}
                footer={{ text: '총계' }}
              />
              <RGDataColumn
                fieldName={'cardNo2'}
                header={{ text: '입고탱크' }}
                width={80}
                numberFormat={'#,##0'}
              />
              <RGDataColumn
                fieldName={'qty'}
                header={{ text: '수량' }}
                width={120}
                numberFormat={'#,##0.0'}
                styleName={'text-right'}
                footer={{
                  expression: 'sum',
                  numberFormat: '#,##0.0',
                  styleName: 'text-right',
                }}
                groupFooter={{ expression: 'sum', numberFormat: '#,##0.0' }}
              />
              <RGDataColumn
                fieldName={'cost'}
                header={{ text: '단가' }}
                width={120}
                numberFormat={'#,##0'}
                styleName={'text-right'}
                footer={{ expression: 'sum', numberFormat: '#,##0' }}
                groupFooter={{ expression: 'sum', numberFormat: '#,##0' }}
              />
              <RGDataColumn
                fieldName={'amount'}
                header={{ text: '금액' }}
                width={120}
                numberFormat={'#,##0'}
                styleName={'text-right'}
                footer={{ expression: 'sum', numberFormat: '#,##0' }}
                groupFooter={{ expression: 'sum', numberFormat: '#,##0' }}
              />
              <RGDataColumn
                fieldName={'oilName'}
                header={{ text: '유종' }}
                width={80}
              />
            </RealGridReact>
          </div>
        </div>

        <div className='mt-4 flex items-center justify-between'>
          <div className='flex items-center space-x-4'>
            <span className='text-sm text-muted-foreground'>
              총 {purchaseData.length}건
            </span>
            {isLoading && (
              <span className='text-sm text-blue-600'>로딩 중...</span>
            )}
          </div>
        </div>
      </ExpandableCard>
    </div>
  );
}
